import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Clock, Users, Copy, Star, Search, TrendingUp, Sparkles, Filter, Heart, X, Refrigerator, Scan, Calendar, Receipt, Wine, UtensilsCrossed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { motion } from "framer-motion";
import ChefAvatar from "@/components/ChefAvatar";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";

const CATEGORIES = [
  { id: "all", label: "Tout", emoji: "🍳" },
  { id: "entree", label: "Entrées", emoji: "🥗" },
  { id: "plat", label: "Plats", emoji: "🍽️" },
  { id: "dessert", label: "Desserts", emoji: "🍰" },
  { id: "boisson", label: "Boissons", emoji: "🥤" },
];

const TAGS = [
  { id: "vege", label: "Végé", emoji: "🥬" },
  { id: "sans_cuisson", label: "Sans cuisson", emoji: "❄️" },
  { id: "rapide", label: "Rapide", emoji: "⚡" },
  { id: "facile", label: "Facile", emoji: "👶" },
];

const DiscoverPage = () => {
  const [recipes, setRecipes] = useState([]);
  const [chefs, setChefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("recipes");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTags, setSelectedTags] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [recipesRes, chefsRes] = await Promise.all([
          axios.get(`${API}/feed?limit=50`),
          axios.get(`${API}/chefs?limit=20`)
        ]);
        setRecipes(recipesRes.data);
        setChefs(chefsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Erreur de chargement");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const toggleTag = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };

  // Filter recipes based on search, category and tags
  const filteredRecipes = recipes.filter(recipe => {
    const matchesSearch = !searchQuery || 
      recipe.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      recipe.chef_pseudo?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === "all" || recipe.category === selectedCategory;
    
    const matchesTags = selectedTags.length === 0 || 
      selectedTags.every(tag => recipe.tags?.includes(tag));
    
    return matchesSearch && matchesCategory && matchesTags;
  });

  const filteredChefs = chefs.filter(chef =>
    chef.pseudo?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 max-w-7xl mx-auto" data-testid="discover-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-charcoal flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-accent" />
          Découvrir
        </h1>
        <p className="text-muted-foreground mt-1">
          Explorez les créations de notre communauté de chefs
        </p>
      </div>

      {/* AI Features CTAs */}
      {/* Row 1: 2 buttons */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        {/* Fridge Scanner CTA */}
        <Link to="/fridge-scanner" className="block">
          <Card className="bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-primary/10 border-cyan-500/30 hover:border-cyan-500/50 transition-all hover:shadow-lg overflow-hidden group h-full">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Refrigerator className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-charcoal text-sm">
                  Scanner frigo
                </h3>
                <p className="text-xs text-muted-foreground truncate">
                  Que cuisiner ?
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Dish Scanner CTA - NEW */}
        <Link to="/dish-scanner" className="block">
          <Card className="bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-rose-500/10 border-purple-500/30 hover:border-purple-500/50 transition-all hover:shadow-lg overflow-hidden group h-full">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <UtensilsCrossed className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-charcoal text-sm flex items-center gap-1">
                  Scanner plat
                  <Badge className="bg-purple-500/20 text-purple-700 hover:bg-purple-500/20 text-[10px] px-1">New</Badge>
                </h3>
                <p className="text-xs text-muted-foreground truncate">
                  Calories & recette
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Row 2: Full width Menu button */}
      <div className="mb-3">
        <Link to="/weekly-menu" className="block">
          <Card className="bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-teal-500/10 border-green-500/30 hover:border-green-500/50 transition-all hover:shadow-lg overflow-hidden group">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-charcoal text-base">
                  Menu de la semaine
                </h3>
                <p className="text-sm text-muted-foreground">
                  Planifiez vos repas et générez votre liste de courses
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Row 3: 2 buttons */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {/* Receipts CTA */}
        <Link to="/my-receipts" className="block">
          <Card className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10 border-amber-500/30 hover:border-amber-500/50 transition-all hover:shadow-lg overflow-hidden group h-full">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Receipt className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-charcoal text-sm">
                  Mes tickets
                </h3>
                <p className="text-xs text-muted-foreground truncate">
                  Rappels produits
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Wine Cellar CTA */}
        <Link to="/wine-cellar" className="block">
          <Card className="bg-gradient-to-r from-red-500/10 via-rose-500/10 to-pink-500/10 border-red-500/30 hover:border-red-500/50 transition-all hover:shadow-lg overflow-hidden group h-full">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-600 to-rose-700 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Wine className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-charcoal text-sm">
                  Ma Cave
                </h3>
                <p className="text-xs text-muted-foreground truncate">
                  Vins & champagnes
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Search Bar */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Rechercher une recette ou un chef..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-12 h-12 rounded-2xl bg-muted/50 border-0 focus-visible:ring-primary"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-2 mb-4 h-12 rounded-2xl bg-muted/50">
          <TabsTrigger 
            value="recipes" 
            className="rounded-xl data-[state=active]:bg-primary data-[state=active]:text-white"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Recettes ({filteredRecipes.length})
          </TabsTrigger>
          <TabsTrigger 
            value="chefs"
            className="rounded-xl data-[state=active]:bg-primary data-[state=active]:text-white"
          >
            <GustoIcon className="w-4 h-4" />
            Chefs ({filteredChefs.length})
          </TabsTrigger>
        </TabsList>

        {/* Recipes Tab */}
        <TabsContent value="recipes" className="mt-0 space-y-4">
          {/* Category Filters */}
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {CATEGORIES.map((cat) => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
                className={`rounded-full whitespace-nowrap flex-shrink-0 ${
                  selectedCategory === cat.id ? 'bg-primary' : ''
                }`}
              >
                <span className="mr-1">{cat.emoji}</span>
                {cat.label}
              </Button>
            ))}
          </div>

          {/* Tag Filters */}
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {TAGS.map((tag) => (
              <Badge
                key={tag.id}
                variant={selectedTags.includes(tag.id) ? "default" : "outline"}
                className={`cursor-pointer whitespace-nowrap flex-shrink-0 ${
                  selectedTags.includes(tag.id) ? 'bg-accent text-white' : 'hover:bg-muted'
                }`}
                onClick={() => toggleTag(tag.id)}
              >
                <span className="mr-1">{tag.emoji}</span>
                {tag.label}
                {selectedTags.includes(tag.id) && (
                  <X className="w-3 h-3 ml-1" />
                )}
              </Badge>
            ))}
          </div>

          {filteredRecipes.length === 0 ? (
            <Card className="p-8 text-center">
              <GustoLogo size="lg" />
              <p className="text-muted-foreground">
                {searchQuery || selectedCategory !== "all" || selectedTags.length > 0
                  ? "Aucune recette trouvée avec ces filtres" 
                  : "Aucune recette pour le moment"}
              </p>
              {(selectedCategory !== "all" || selectedTags.length > 0) && (
                <Button 
                  variant="link" 
                  onClick={() => { setSelectedCategory("all"); setSelectedTags([]); }}
                  className="mt-2"
                >
                  Réinitialiser les filtres
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredRecipes.map((recipe, index) => (
                <motion.div
                  key={recipe.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link to={`/recipe/${recipe.id}`}>
                    <Card className="recipe-card overflow-hidden group cursor-pointer h-full">
                      <div className="aspect-[4/3] bg-gradient-to-br from-primary/20 to-accent/30 relative">
                        {recipe.final_image ? (
                          <img 
                            src={recipe.final_image.startsWith('data:') ? recipe.final_image : `data:image/jpeg;base64,${recipe.final_image}`}
                            alt={recipe.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <GustoLogo size="lg" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                        
                        {/* Chef badge with toque */}
                        <Link 
                          to={`/chef/${recipe.chef_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="absolute top-3 left-3 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-full pl-1 pr-3 py-1 hover:bg-white transition-colors"
                        >
                          <ChefAvatar
                            src={recipe.chef_avatar}
                            name={recipe.chef_pseudo}
                            size="xs"
                            showToque={true}
                          />
                          <span className="text-xs font-medium text-charcoal">{recipe.chef_pseudo}</span>
                        </Link>

                        {/* Reproductions badge */}
                        <div className="absolute top-3 right-3 flex gap-1">
                          {recipe.category && (
                            <Badge className="bg-white/90 text-charcoal text-xs">
                              {CATEGORIES.find(c => c.id === recipe.category)?.emoji || "🍳"}
                            </Badge>
                          )}
                          {recipe.likes_count > 0 && (
                            <Badge className="bg-white/90 text-red-500">
                              <Heart className="w-3 h-3 mr-1 fill-red-500" />
                              {recipe.likes_count}
                            </Badge>
                          )}
                        </div>

                        {/* Recipe name overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          {/* Tags */}
                          {recipe.tags?.length > 0 && (
                            <div className="flex gap-1 mb-2 flex-wrap">
                              {recipe.tags.slice(0, 2).map(tag => (
                                <Badge key={tag} variant="secondary" className="text-[10px] bg-white/20 text-white border-0">
                                  {TAGS.find(t => t.id === tag)?.emoji} {TAGS.find(t => t.id === tag)?.label}
                                </Badge>
                              ))}
                            </div>
                          )}
                          <h3 className="font-heading font-bold text-lg text-white line-clamp-2">
                            {recipe.name}
                          </h3>
                        </div>
                      </div>
                      
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {(recipe.prep_time || 0) + (recipe.cook_time || 0)} min
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {recipe.servings} parts
                          </span>
                          {recipe.steps?.length > 0 && (
                            <span className="flex items-center gap-1 text-primary">
                              {recipe.steps.length} étapes
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Chefs Tab */}
        <TabsContent value="chefs" className="mt-0">
          {filteredChefs.length === 0 ? (
            <Card className="p-8 text-center">
              <GustoLogo size="lg" />
              <p className="text-muted-foreground">
                {searchQuery ? "Aucun chef trouvé" : "Aucun chef pour le moment"}
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredChefs.map((chef, index) => (
                <motion.div
                  key={chef.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link to={`/chef/${chef.id}`}>
                    <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer group">
                      <div className="flex items-center gap-4">
                        <div className="group-hover:scale-105 transition-transform">
                          <ChefAvatar
                            src={chef.avatar}
                            name={chef.pseudo}
                            size="lg"
                            showToque={true}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-semibold text-lg text-charcoal truncate group-hover:text-primary transition-colors">
                            Chef(fe) {chef.pseudo}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {chef.recipes_count || 0} recette{(chef.recipes_count || 0) > 1 ? 's' : ''}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              <GustoIcon className="w-4 h-4" />
                              Chef Impro
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DiscoverPage;
